import { get } from 'lodash';
import {
  financeService,
  MS_PHASE_3_ENDPOINTS,
} from 'utils/msEndpointConstants';
import {
  convertObjToCamelCase,
  retrieveListOrObject,
  retrieveSortForMS,
} from 'utils';
import moment from 'moment';
import { DEFAULT_ERROR, SAVE_DATE_FORMAT } from '../utils/constants';
import DatadogHandler from '../utils/datadog';
import { transformDiscountGroups } from '../utils/ms-utils/ms-phase-3/FinanceUtils';

const fileName = 'src/services/DiscountService.js';
const { FINANCE } = MS_PHASE_3_ENDPOINTS;

export const msFetchDiscountGroups = async (_, reqData) => {
  try {
    const financeSvc = financeService();
    const { schoolID, name, discountItemGroupFilter, pagination } = reqData;

    const body = {
      school_id: schoolID,
      ...(name && { name }),
      ...(discountItemGroupFilter && {
        filter: {
          ...(discountItemGroupFilter?.isRecurrent && {
            is_recurrent: discountItemGroupFilter.isRecurrent.map(item => ({
              eq: item.eq,
            })),
          }),
        },
      }),
    };

    const params = {
      ...(pagination?.page && { page: pagination.page }),
      ...(pagination?.perPage && { perPage: pagination.perPage }),
      ...(pagination?.sort && retrieveSortForMS(pagination.sort)),
    };

    const resp = await financeSvc.post(
      ...FINANCE.getAllDiscountItemGroup(body, params)
    );

    const discountGroups = get(resp, 'data.data', []);
    const discountGroupIds = discountGroups.map(group => group.id);

    let centreRelationsMap = {};

    if (discountGroupIds.length > 0) {
      const centreRelationsResp = await financeSvc.post(
        ...FINANCE.getAllDiscountItemGroupCentreRelations(discountGroupIds)
      );

      const centreRelationsData = get(
        centreRelationsResp,
        'data.discount_item_group_centre_relations',
        {}
      );
      centreRelationsMap = centreRelationsData;
    }
    const transformedData = transformDiscountGroups(
      discountGroups,
      centreRelationsMap
    );

    return {
      data: {
        getAllDiscountItemGroup: {
          data: transformedData,
          totalCount: get(resp, 'data.totalCount', 0),
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchDiscountGroups',
        fileName,
      },
      'error'
    );
    return {
      data: {
        getAllDiscountItemGroup: {
          data: [],
          totalCount: 0,
        },
      },
      success: false,
      error:
        get(error, 'response.data.error') ||
        get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msDeactivateDiscount = async (_, reqData) => {
  try {
    const resp = await financeService().post(
      FINANCE.deactivateDiscountItemGroup,
      {
        id: get(reqData, 'IDDiscountItemGroup'),
        remarks: get(reqData, 'remarks'),
        cease_existing_arrangement: get(reqData, 'ceaseExistingArrangement'),
      }
    );
    return {
      success: !!get(resp, 'data.id'),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msDeactivateDiscount',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msActivateDiscount = async (_, reqData) => {
  try {
    const resp = await financeService().post(
      FINANCE.activateDiscountItemGroup,
      {
        id: get(reqData, 'IDDiscountItemGroup'),
        effective_date: get(reqData, 'effectiveDate')
          ? moment(get(reqData, 'effectiveDate'))
              .endOf('day')
              .format(SAVE_DATE_FORMAT)
          : null,
        ceased_date: get(reqData, 'ceasedDate')
          ? moment(get(reqData, 'ceasedDate'))
              .endOf('day')
              .format(SAVE_DATE_FORMAT)
          : null,
      }
    );
    return {
      success: !!get(resp, 'data.id'),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msActivateDiscount',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msAddNewDiscountGroup = async (_, reqData) => {
  try {
    const params = { ...reqData, Amount: Number(get(reqData, 'Amount')) };
    const response = await financeService().post(
      ...FINANCE.addDiscountGroup(params)
    );

    return {
      data: {
        addDiscountItemGroup: convertObjToCamelCase(get(response, 'data', {})),
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddNewDiscountGroup ',
        fileName,
      },
      'error'
    );
    return {
      data: {
        addDiscountItemGroup: null,
      },
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getDiscountItemGroupByIds = (ids = []) => {
  if (!ids.length) return {};
  return financeService()
    .post(...FINANCE.getDiscountItemGroupByIds(ids))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const msChangeObsoleteDateChildDiscount = async (_, reqData) => {
  try {
    const resp = await financeService().post(
      FINANCE.changeObsoleteDateChildDiscount,
      {
        child_id: get(reqData, 'IDChild'),
        discount_arrangement_id: get(reqData, 'IDDiscountArrangement'),
        remarks: get(reqData, 'remark', ''),
        to_date: moment(get(reqData, 'to'))
          .endOf('day')
          .format(SAVE_DATE_FORMAT),
      }
    );
    return {
      success: get(resp, 'data.is_success', false),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msChangeObsoleteDateChildDiscount',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};
